/**
 * Settings → Usage — V5-07 §4.1.
 *
 * Read-only numbers. Deliberately no controls: quotas are the platform's to
 * set, and putting a button here would imply a self-serve upgrade that does not
 * exist yet. "Need more?" is an email to a human, which is what it actually is.
 */

import { useQuery } from '@tanstack/react-query';
import { HardDrive, Mail, MessageCircle, TriangleAlert } from 'lucide-react';
import { PageHeader } from '@/app/layout/AppShell';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

interface UsageResponse {
  plan: string;
  quotas: { storageMB: number; emailsPerMonth: number; whatsappPerMonth: number };
  storage: {
    usedBytes: number;
    limitBytes: number;
    byProject: { projectId: string; name: string; key: string; bytes: number }[];
  };
  email: { sent: number; limit: number };
  whatsapp: { sent: number; limit: number };
  period: string;
  periodEndsAt: string;
  channels: { email: string; whatsapp: string; storage: string };
  warnings: { kind: string; used: number; limit: number; percentage: number }[];
}

function mb(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Green under 80%, amber to 100%, red at the limit — the workload palette. */
function barColour(percentage: number): string {
  if (percentage >= 100) return 'var(--danger)';
  if (percentage >= 80) return 'var(--warn)';
  return 'var(--accent)';
}

function Meter({
  icon,
  label,
  used,
  limit,
  format,
  source,
  testId,
}: {
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number;
  format: (n: number) => string;
  source?: string;
  testId: string;
}) {
  const percentage = limit > 0 ? Math.round((used / limit) * 100) : 0;

  return (
    <section className="vt-surface flex flex-col gap-1.5 p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5">
        {icon}
        <h2 className="flex-1 text-[13px] font-medium">{label}</h2>
        {source && (
          <Badge tone={source === 'own' ? 'accent' : undefined}>
            {source === 'own' ? 'your key' : 'our key'}
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className="h-2 w-full overflow-hidden rounded-full bg-[var(--surface-2)]">
          <div
            className="h-full rounded-full"
            style={{ width: `${Math.min(percentage, 100)}%`, background: barColour(percentage) }}
          />
        </div>
        <span
          className="vt-mono w-[52px] shrink-0 text-right text-[12px] font-medium"
          style={{ color: barColour(percentage) }}
          data-testid={`${testId}-percentage`}
        >
          {percentage}%
        </span>
      </div>

      <p className="vt-mono text-[12px] vt-muted" data-testid={`${testId}-numbers`}>
        {format(used)} of {format(limit)}
      </p>
    </section>
  );
}

export function UsagePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['workspace-usage'],
    queryFn: async () => (await api.get<UsageResponse>('/settings/usage')).data,
  });

  if (isLoading || !data) {
    return (
      <>
        <PageHeader title="Usage" />
        <div className="p-4">
          <Skeleton className="h-64" />
        </div>
      </>
    );
  }

  const resetDate = new Date(data.periodEndsAt).toLocaleDateString();

  return (
    <>
      <PageHeader title="Usage" />

      <div className="flex flex-col gap-4 p-4" data-testid="usage-page">
        {data.warnings.length > 0 && (
          <div
            className="flex items-start gap-2 rounded-[6px] border border-[var(--warn)] p-3 text-[12px]"
            data-testid="quota-warning-banner"
          >
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--warn)]" />
            <div>
              <p className="font-medium">
                {data.warnings.map((w) => w.kind).join(' and ')} above 80%
              </p>
              <p className="vt-muted">
                Nothing has stopped working. At 100%, uploads and notification email pause until{' '}
                {resetDate} or the limit is raised.
              </p>
            </div>
          </div>
        )}

        <div className="grid gap-2 sm:grid-cols-3">
          <Meter
            testId="usage-storage"
            icon={<HardDrive className="size-3.5" />}
            label="Storage"
            used={data.storage.usedBytes}
            limit={data.storage.limitBytes}
            format={mb}
            source={data.channels.storage}
          />
          <Meter
            testId="usage-email"
            icon={<Mail className="size-3.5" />}
            label="Emails this month"
            used={data.email.sent}
            limit={data.email.limit}
            format={(n) => String(n)}
            source={data.channels.email}
          />
          <Meter
            testId="usage-whatsapp"
            icon={<MessageCircle className="size-3.5" />}
            label="WhatsApp this month"
            used={data.whatsapp.sent}
            limit={data.whatsapp.limit}
            format={(n) => String(n)}
            source={data.channels.whatsapp}
          />
        </div>

        <p className="text-[11px] vt-muted">
          Monthly counters reset on {resetDate}. Invitations, email verification and password
          resets never count against your allowance. Anything sent on your own keys is billed by
          your provider, not counted here.
        </p>

        {data.storage.byProject.length > 0 && (
          <section className="vt-surface p-3" data-testid="usage-by-project">
            <h2 className="mb-1.5 text-[13px] font-medium">Storage by project</h2>
            <table className="w-full text-left text-[12px]">
              <tbody>
                {data.storage.byProject.map((row) => (
                  <tr key={row.projectId} className="border-t border-[var(--border)] first:border-0">
                    <td className="py-1">
                      {row.name} <span className="vt-mono vt-muted">{row.key}</span>
                    </td>
                    <td className="vt-mono py-1 text-right">{mb(row.bytes)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        <p className="text-[12px]">
          Need more?{' '}
          <a
            className="text-[var(--accent)] hover:underline"
            href={`mailto:hello@lauruss.com?subject=${encodeURIComponent('VirtueTasks — raise our plan limits')}`}
          >
            Contact us
          </a>
        </p>
      </div>
    </>
  );
}
